import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Lightning,
  ListChecks,
  CheckCircle,
  Circle,
  ArrowSquareOut,
  Question,
  Note as NoteIcon,
  Briefcase,
  X as XIcon,
  PencilSimple,
  Check,
  Plus,
} from "@phosphor-icons/react";
import { entryText, keyPointParts } from "@/lib/structuredEntry";

// Iter 85.82 · Bulk-edit registry. Each EditableTextField registers a
// commit ref keyed by its path so OrganizedNoteBody can fan out saves
// in parallel when the advisor clicks "Done". Individual click-to-edit
// still works when bulkEditing=false.
const BulkEditContext = createContext(null);

// Iter 85.72 · Small inline editor used across every narrative section
// (summary, decisions, key_points, follow_ups, open_questions, deliverables).
// Iter 85.81 · Discoverability — pencil always visible + whole row is a
// button; click anywhere on a section to open the editor.
// Iter 85.82 · Bulk Edit Mode — top-of-note "Edit" toggle puts EVERY
// section into edit mode at once; "Done" commits all dirty drafts in
// parallel (each POSTs /notes/{id}/edit-text so audit history is
// preserved section by section) then exits. Advisors asked for a
// mode-based workflow rather than click-per-section inline edits.
// Saves via /notes/{id}/edit-text so the backend records revision
// provenance on `edit_history[]`.
function EditableTextField({
  noteId, path, initialValue, field = null,
  className = "", multiline = true,
  onSaved, testIdPrefix,
  placeholder = "",
}) {
  const bulk = useContext(BulkEditContext);
  const bulkEditing = !!bulk?.bulkEditing;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialValue || "");
  const [saving, setSaving] = useState(false);

  // Iter 85.82 · Bulk mode — when the parent flips bulkEditing on, every
  // field silently enters edit mode with the current value seeded. On
  // toggle-off, the parent triggers a commit sweep via a shared ref
  // (see OrganizedNoteBody). Individual click-to-edit still works
  // outside bulk mode.
  const initialSeed = useRef(initialValue || "");
  useEffect(() => {
    if (bulkEditing) {
      setDraft(initialValue || "");
      initialSeed.current = initialValue || "";
      setEditing(true);
    } else {
      // Exiting bulk mode — leave `editing` as-is; the parent has
      // already awaited any pending commits. Reset local edit state
      // only if we're not mid-save.
      if (!saving) {
        setEditing(false);
        setDraft(initialValue || "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkEditing]);

  // Keep the local draft in sync when the note reloads with fresh data
  // from the server AND we're not actively editing this field.
  useEffect(() => {
    if (!editing) setDraft(initialValue || "");
  }, [initialValue, editing]);

  const openEdit = () => {
    setDraft(initialValue || "");
    setEditing(true);
  };
  const cancel = () => { setEditing(false); setDraft(initialValue || ""); };
  const save = async () => {
    const next = (draft || "").trim();
    if (next === (initialValue || "").trim()) { cancel(); return { changed: false }; }
    setSaving(true);
    try {
      const { data } = await api.post(`/notes/${noteId}/edit-text`, {
        path, value: next, ...(field ? { field } : {}),
      });
      if (data?.changed) {
        // Iter 85.82 · In bulk mode, defer local mutation to the
        // parent commitBulk refetch to avoid a stale-closure race
        // where parallel applyChange calls overwrite each other.
        if (!bulkEditing) {
          onSaved?.(next, data);
          toast.success("Saved");
        }
      }
      if (!bulkEditing) setEditing(false);
      return { changed: !!data?.changed, next };
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return { changed: false, error: e };
    } finally { setSaving(false); }
  };

  // Iter 85.82 · Register a commit ref keyed by path so the parent
  // "Done" handler can fan out saves across every dirty field. The ref
  // itself is stable; its `.current` reads the latest draft closure.
  const commitRef = useRef(null);
  commitRef.current = save;
  useEffect(() => {
    if (!bulk) return;
    const key = `${path}${field ? "::" + field : ""}`;
    bulk.register(key, commitRef);
    return () => bulk.unregister(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulk, path, field]);

  if (editing) {
    const InputEl = multiline ? "textarea" : "input";
    return (
      <div className="group" data-testid={`${testIdPrefix}-editor`}>
        <InputEl
          data-testid={`${testIdPrefix}-input`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") { e.preventDefault(); cancel(); }
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
          }}
          placeholder={placeholder}
          rows={multiline ? Math.max(2, Math.min(10, (draft || "").split("\n").length + 1)) : undefined}
          className={`w-full bg-background border border-primary rounded-none px-2 py-1 text-sm font-sans normal-case tracking-normal outline-none ${className}`}
          autoFocus={!bulkEditing}
        />
        {!bulkEditing && (
          <div className="mt-1 flex items-center gap-3 text-[0.625rem] font-mono uppercase tracking-widest">
            <button
              type="button"
              data-testid={`${testIdPrefix}-save`}
              onClick={save}
              disabled={saving}
              className="text-brass hover:opacity-80 disabled:opacity-40 inline-flex items-center gap-1"
            >
              <Check size={11} weight="bold" /> {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              data-testid={`${testIdPrefix}-cancel`}
              onClick={cancel}
              className="text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
            <span className="text-muted-foreground/60 normal-case tracking-normal font-sans italic">⌘/Ctrl+Enter to save · Esc to cancel</span>
          </div>
        )}
      </div>
    );
  }

  // Iter 85.81 · Display mode = click-to-edit. Entire row is a button so
  // tapping anywhere in the text opens the editor. Pencil stays visible
  // at reduced opacity so the affordance is obvious without shouting.
  return (
    <button
      type="button"
      onClick={openEdit}
      data-testid={`${testIdPrefix}-display`}
      title="Click to edit"
      className={`group w-full text-left inline-flex items-start gap-1.5 rounded-none px-1 -mx-1 py-0.5 hover:bg-primary/5 focus:bg-primary/5 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/40 transition-colors cursor-text`}
    >
      <span className={`${className} min-w-0 flex-1 whitespace-pre-wrap`}>
        {initialValue || <span className="text-muted-foreground italic">{placeholder || "—"}</span>}
      </span>
      <span
        data-testid={`${testIdPrefix}-edit`}
        aria-hidden="true"
        className="opacity-40 group-hover:opacity-100 group-focus:opacity-100 transition-opacity text-primary shrink-0 mt-0.5"
      >
        <PencilSimple size={12} weight="bold" />
      </span>
    </button>
  );
}


// Iter 85.16 · Per-item reject affordance for the merged Organize
// review flow. Compact ✕ button; parent handles the confirm + POST.
function RejectButton({ onClick, testId, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={label}
      className="opacity-30 hover:opacity-100 hover:text-destructive transition-opacity text-muted-foreground shrink-0"
    >
      <XIcon size={12} weight="bold" />
    </button>
  );
}

const MOVEMENT_LABELS = {
  listen: "Listen",
  reveal: "Reveal",
  reimagine: "Reimagine",
  guide: "Guide",
};
const MOVEMENT_ORDER = ["listen", "reveal", "reimagine", "guide"];

/**
 * Iter 81 · Individual open-question row. Renders text + optional
 * Embrace movement chip + inline movement classifier + resolve toggle.
 * Read/writes the parent note's meeting_structured.open_questions[i]
 * via the shared applyChange pathway so the surrounding surfaces
 * (Embrace rollup, morning brief) pick it up on next fetch.
 */
function OpenQuestionRow({ idx, entry, noteId, onLocalUpdate, onReject, onTextSaved }) {
  const [movementEditing, setMovementEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const dictEntry = typeof entry === "string"
    ? { text: entry }
    : (entry || { text: "" });
  const text = dictEntry.text || dictEntry.title || "";
  const movement = dictEntry.movement || null;
  const resolved = !!dictEntry.resolved;

  const patchMovement = async (mv) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/notes/${noteId}/open-questions/${idx}`, {
        movement: mv,
      });
      onLocalUpdate?.(data.open_question);
      toast.success(mv ? `Classified as ${MOVEMENT_LABELS[mv]}` : "Movement cleared");
      setMovementEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleResolved = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/notes/${noteId}/open-questions/${idx}/resolve`, {
        resolved: !resolved,
      });
      onLocalUpdate?.(data.open_question);
      toast.success(!resolved ? "Question marked resolved" : "Question reopened");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className={`border-l-2 pl-3 font-serif text-sm leading-relaxed ${
        resolved ? "border-emerald-500/40 opacity-70" : "border-primary/40"
      }`}
      data-testid={`note-viewer-open-question-${idx}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className={`flex-1 min-w-0 ${resolved ? "line-through" : ""}`}>
          <EditableTextField
            noteId={noteId}
            path={`open_questions.${idx}`}
            initialValue={text}
            testIdPrefix={`note-viewer-open-question-${idx}-text`}
            onSaved={(next) => onTextSaved?.(next)}
          />
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <button
            type="button"
            data-testid={`note-viewer-open-question-${idx}-resolve`}
            onClick={toggleResolved}
            disabled={busy}
            className={`text-[0.625rem] font-mono uppercase tracking-widest disabled:opacity-40 ${
              resolved ? "text-muted-foreground hover:text-primary" : "text-muted-foreground hover:text-emerald-500"
            }`}
            title={resolved ? "Reopen this question" : "Mark this question resolved"}
          >
            {resolved ? "Reopen" : "Resolve"}
          </button>
          {onReject && !resolved && (
            <RejectButton
              onClick={onReject}
              testId={`note-viewer-open-question-${idx}-reject`}
              label="Remove this open question — Re-organize won't bring it back"
            />
          )}
        </div>
      </div>
      <div className="mt-1 flex items-center gap-2 flex-wrap text-[0.625rem] font-mono uppercase tracking-widest">
        {movementEditing ? (
          <div className="flex items-center gap-1 flex-wrap" data-testid={`note-viewer-open-question-${idx}-movement-picker`}>
            {MOVEMENT_ORDER.map((mv) => (
              <button
                key={mv}
                type="button"
                onClick={() => patchMovement(mv)}
                disabled={busy}
                data-testid={`note-viewer-open-question-${idx}-movement-${mv}`}
                className={`border px-1.5 py-0.5 hover:border-primary hover:text-primary transition-colors ${
                  movement === mv ? "border-brass text-brass" : "border-border text-muted-foreground"
                }`}
              >
                {MOVEMENT_LABELS[mv]}
              </button>
            ))}
            {movement && (
              <button
                type="button"
                onClick={() => patchMovement(null)}
                disabled={busy}
                data-testid={`note-viewer-open-question-${idx}-movement-clear`}
                className="text-muted-foreground hover:text-destructive px-1.5"
              >
                clear
              </button>
            )}
            <button
              type="button"
              onClick={() => setMovementEditing(false)}
              className="text-muted-foreground hover:text-foreground px-1.5"
            >
              done
            </button>
          </div>
        ) : movement ? (
          <button
            type="button"
            onClick={() => setMovementEditing(true)}
            data-testid={`note-viewer-open-question-${idx}-movement-chip`}
            className="border border-brass/60 text-brass hover:opacity-80 px-1.5 py-0.5"
            title="Edit Embrace movement classification"
          >
            {MOVEMENT_LABELS[movement]}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setMovementEditing(true)}
            data-testid={`note-viewer-open-question-${idx}-movement-classify`}
            className="text-muted-foreground hover:text-brass italic normal-case tracking-normal font-sans text-xs"
          >
            + classify by Embrace movement
          </button>
        )}
        {resolved && dictEntry.resolved_at && (
          <span className="text-muted-foreground" data-testid={`note-viewer-open-question-${idx}-resolved-meta`}>
            resolved {String(dictEntry.resolved_at).slice(0, 10)}
          </span>
        )}
      </div>
    </li>
  );
}

// Iter 85.83 · Action-item participation in bulk Edit mode. When the
// parent OrganizedNoteBody flips bulkEditing on, this component swaps
// the whole title/owner/due row into three simultaneously-editable
// inputs and registers a single commit function on the bulk registry.
// Commit fires PATCH /notes/{id}/action-items/{idx} which now
// bi-directionally propagates title/owner/due to the linked canonical
// Task (see routers/commitments.py). Individual click-to-edit paths
// stay untouched — this component only renders when bulkEditing=true.
function ActionItemBulkEditor({ noteId, idx, action, onSaved }) {
  const bulk = useContext(BulkEditContext);
  const bulkEditing = !!bulk?.bulkEditing;
  const initial = useMemo(() => ({
    task: action?.task || "",
    owner: action?.owner === "unassigned" ? "" : (action?.owner || ""),
    due: action?.due || "",
  }), [action?.task, action?.owner, action?.due]);
  const [draft, setDraft] = useState(initial);
  useEffect(() => { if (bulkEditing) setDraft(initial); }, [bulkEditing, initial]);

  const commitRef = useRef(null);
  commitRef.current = async () => {
    const diff = {};
    if ((draft.task || "").trim() !== (initial.task || "").trim()) {
      const t = (draft.task || "").trim();
      if (!t) return { changed: false };
      diff.title = t;
    }
    const nextOwner = (draft.owner || "").trim();
    const prevOwner = (initial.owner || "").trim();
    if (nextOwner !== prevOwner) diff.owner = nextOwner || "";
    const nextDue = (draft.due || "").trim();
    const prevDue = (initial.due || "").trim();
    if (nextDue !== prevDue) diff.due = nextDue || null;
    if (Object.keys(diff).length === 0) return { changed: false };
    const { data } = await api.patch(`/notes/${noteId}/action-items/${idx}`, diff);
    onSaved?.(data?.action_item);
    return { changed: true };
  };

  useEffect(() => {
    if (!bulk) return;
    const key = `action_items.${idx}::bulk`;
    bulk.register(key, commitRef);
    return () => bulk.unregister(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulk, idx]);

  if (!bulkEditing) return null;
  return (
    <div className="flex-1 space-y-2" data-testid={`note-viewer-action-item-${idx}-bulk-editor`}>
      <textarea
        data-testid={`note-viewer-action-item-${idx}-bulk-title`}
        value={draft.task}
        onChange={(e) => setDraft((d) => ({ ...d, task: e.target.value }))}
        rows={Math.max(1, Math.min(4, (draft.task || "").split("\n").length))}
        className="w-full bg-background border border-primary rounded-none px-2 py-1 text-sm font-sans normal-case tracking-normal outline-none"
        placeholder="Action item title"
      />
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          data-testid={`note-viewer-action-item-${idx}-bulk-owner`}
          value={draft.owner}
          onChange={(e) => setDraft((d) => ({ ...d, owner: e.target.value }))}
          placeholder="owner"
          className="bg-background border border-border rounded-none px-2 py-1 text-xs font-sans normal-case tracking-normal w-40"
        />
        <input
          type="date"
          data-testid={`note-viewer-action-item-${idx}-bulk-due`}
          value={draft.due || ""}
          onChange={(e) => setDraft((d) => ({ ...d, due: e.target.value }))}
          className="bg-background border border-border rounded-none px-2 py-1 text-xs font-sans normal-case tracking-normal"
        />
      </div>
    </div>
  );
}


/**
 * Iter 90 · AddItemRow — inline "+ Add" affordance for manual augmentation
 * of an organized section (Decisions, Action items, Key points, Follow-ups,
 * Open questions, Deliverables). Collapsed by default as a subtle button;
 * expands into a single-line textarea with Save/Cancel. Enter saves, Esc
 * cancels. Deliberately renders with the same typography as the section
 * body so an advisor-added row is visually indistinguishable from an
 * AI-organized row (partner ask: treat as one unified record).
 */
function AddItemRow({ onAdd, label, testIdPrefix }) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (expanded) inputRef.current?.focus();
  }, [expanded]);

  const commit = async () => {
    const raw = draft.trim();
    if (!raw || saving) { setExpanded(false); setDraft(""); return; }
    setSaving(true);
    const result = await onAdd(raw);
    setSaving(false);
    if (result) {
      setDraft("");
      setExpanded(false);
    }
  };
  const cancel = () => { setDraft(""); setExpanded(false); };

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        data-testid={`${testIdPrefix}-add-open`}
        className="mt-2 inline-flex items-center gap-1.5 text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors"
      >
        <Plus size={11} weight="bold" /> Add {label}
      </button>
    );
  }
  return (
    <div className="mt-2 flex items-start gap-2 border-l-2 border-brass/50 pl-3" data-testid={`${testIdPrefix}-add-row`}>
      <textarea
        ref={inputRef}
        rows={1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        placeholder={`New ${label}…`}
        className="flex-1 bg-background border border-border rounded-none px-2 py-1.5 text-sm font-serif focus:outline-none focus:border-primary"
        data-testid={`${testIdPrefix}-add-input`}
      />
      <button
        type="button"
        onClick={commit}
        disabled={saving || !draft.trim()}
        className="shrink-0 text-primary hover:text-primary/80 disabled:opacity-40 p-1"
        title="Add (Enter)"
        aria-label="Add"
        data-testid={`${testIdPrefix}-add-save`}
      >
        <Check size={14} weight="bold" />
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={saving}
        className="shrink-0 text-muted-foreground hover:text-destructive p-1"
        title="Cancel (Esc)"
        aria-label="Cancel"
        data-testid={`${testIdPrefix}-add-cancel`}
      >
        <XIcon size={14} weight="bold" />
      </button>
    </div>
  );
}


/**
 * payload. Extracted from NoteViewerDialog so the same interactive Action Items,
 * Decisions, Key Points, Open Questions, Follow-ups and Suggested Engagements
 * surface identically in every Note Viewer entry point — the full-screen
 * NoteViewerDialog AND the inline MeetingNoteCard on the Briefing Studio list.
 *
 * The one and only interaction contract:
 *   · action_items support completion toggle, inline owner/due edit and
 *     Promote-to-Task with a linked Task chip once promoted.
 *   · engagement_seeds support "Spawn briefing" once, then a briefing link.
 *   · all list rows tolerate legacy string OR Iter-74 dict shape via
 *     `entryText()` / `keyPointParts()`.
 *
 * Section order (canonical Iter 79):
 *   Summary → Decisions → Action items → Key points → Open questions →
 *   Follow-ups → Suggested engagements.
 *
 * Empty sections do not render. Raw notes and Source Materials live in the
 * parent surface because they are not part of `meeting_structured`.
 */
export default function OrganizedNoteBody({ note, onNoteChange, focusCommitmentIdx = null, actionItemRefs = null }) {
  const [promotingIdx, setPromotingIdx] = useState(null);
  const [spawningIdx, setSpawningIdx] = useState(null);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editDraft, setEditDraft] = useState({ owner: "", due: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [togglingIdx, setTogglingIdx] = useState(null);
  // Iter 85.8 · Editable Action Title — separate state so title editing
  // is its own tap surface, independent of the owner/due metadata edit.
  const [titleEditingIdx, setTitleEditingIdx] = useState(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const localRefs = useRef({});
  const refs = actionItemRefs || localRefs;

  // Iter 85.82 · Bulk edit mode — advisors asked for a single "Edit"
  // toggle at the top that puts every section into edit mode at once,
  // then a "Done" button that commits all dirty drafts in parallel.
  // Individual click-to-edit still works when bulkEditing=false.
  const [bulkEditing, setBulkEditing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const commitRegistry = useRef(new Map());
  const bulkCtx = useMemo(() => ({
    bulkEditing,
    register: (key, ref) => { commitRegistry.current.set(key, ref); },
    unregister: (key) => { commitRegistry.current.delete(key); },
  }), [bulkEditing]);
  const enterBulk = () => setBulkEditing(true);
  const cancelBulk = () => setBulkEditing(false);
  const commitBulk = async () => {
    if (committing) return;
    setCommitting(true);
    try {
      const entries = Array.from(commitRegistry.current.entries());
      const results = await Promise.allSettled(
        entries.map(async ([, ref]) => {
          const fn = ref?.current;
          if (typeof fn !== "function") return { changed: false };
          return await fn();
        })
      );
      const changed = results.filter((r) => r.status === "fulfilled" && r.value?.changed).length;
      const failed = results.filter((r) => r.status === "rejected").length;
      // Iter 85.82 · Refetch the note so every section reflects its
      // freshly-saved text in one atomic UI update. This sidesteps the
      // stale-closure race that came from parallel applyChange calls
      // each spreading their own pre-edit copy of `note`. Backend is
      // already source-of-truth; the network cost is a single GET.
      if (changed > 0 && note?.id) {
        try {
          const { data } = await api.get(`/notes/${note.id}`);
          if (data) applyChange(data);
        } catch (e) {
          // Refetch failure is non-fatal — the individual saves already
          // hit the server. Advisor will see the fresh state on next
          // reload. Show a soft warning but keep the toast honest.
          toast.error("Saved, but couldn't refresh the view. Reload to sync.");
        }
      }
      if (failed > 0) {
        toast.error(`${failed} section${failed === 1 ? "" : "s"} failed to save. Try again.`);
      } else if (changed > 0) {
        toast.success(`Saved ${changed} section${changed === 1 ? "" : "s"}.`);
      } else {
        toast("No changes to save.");
      }
      setBulkEditing(false);
    } finally {
      setCommitting(false);
    }
  };

  if (!note) return null;
  const ms = note.meeting_structured || {};
  const applyChange = (next) => onNoteChange?.(next);

  // Iter 90 · Manual augmentation — append a new advisor-authored entry
  // to a structured section without regenerating the organized note.
  // Handled inline so the AddItemRow below can call it with the section
  // key and raw text; the server wraps it in the right entry shape
  // (task / body / title / text) based on the section.
  const addItem = async (section, value) => {
    const raw = String(value || "").trim();
    if (!raw) return null;
    try {
      const { data } = await api.post(`/notes/${note.id}/add-structured-item`, { section, value: raw });
      const rows = [...(ms[section] || []), data.entry];
      applyChange({ ...note, meeting_structured: { ...ms, [section]: rows } });
      toast.success("Added");
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
      return null;
    }
  };

  // Iter 85.16 · Per-item reject/delete. Removes the entry from
  // meeting_structured.{section}[] and stamps the normalized signature
  // on `_rejected_signatures` server-side so a subsequent Re-organize
  // won't silently re-propose it. Idempotent — a 404 back means the
  // row was already gone; we refresh local state anyway.
  const rejectItem = async (section, idx, labelForToast) => {
    if (!confirm(`Remove this ${labelForToast} from the organized note?`)) return;
    try {
      await api.delete(`/notes/${note.id}/meeting-structured/${section}/${idx}`);
      const rows = [...((note.meeting_structured || {})[section] || [])];
      rows.splice(idx, 1);
      applyChange({
        ...note,
        meeting_structured: { ...(note.meeting_structured || {}), [section]: rows },
      });
      toast.success(`${labelForToast[0].toUpperCase() + labelForToast.slice(1)} removed. Re-organize won't bring it back.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  // Iter 80 · Completion toggle wired to /complete. Backend flips both the
  // action_item `done` flag and any linked Task's status in a single call
  // (Iter 76 always-on bi-directional sync). We update optimistically and
  // trust the server contract; the surrounding surface picks up the change
  // on its next fetch.
  const toggleComplete = async (idx, currentDone) => {
    if (togglingIdx !== null) return;
    setTogglingIdx(idx);
    try {
      await api.post(`/notes/${note.id}/action-items/${idx}/complete`, { done: !currentDone });
      const actions = [...((note.meeting_structured || {}).action_items || [])];
      actions[idx] = { ...actions[idx], done: !currentDone };
      applyChange({
        ...note,
        meeting_structured: { ...(note.meeting_structured || {}), action_items: actions },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setTogglingIdx(null);
    }
  };

  const onPromoteToTask = async (idx) => {
    if (promotingIdx !== null) return;
    setPromotingIdx(idx);
    try {
      const { data } = await api.post(`/notes/${note.id}/action-items/${idx}/promote-to-task`);
      const actions = [...((note.meeting_structured || {}).action_items || [])];
      actions[idx] = {
        ...actions[idx],
        promoted_task_id: data?.task?.id,
        promoted_at: data?.task ? new Date().toISOString() : actions[idx].promoted_at,
      };
      applyChange({
        ...note,
        meeting_structured: { ...(note.meeting_structured || {}), action_items: actions },
      });
      toast.success(data?.already_promoted ? "Task already linked to this action item." : "Promoted to Task.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote to Task failed");
    } finally {
      setPromotingIdx(null);
    }
  };

  const openEdit = (idx, action) => {
    setEditingIdx(idx);
    setEditDraft({ owner: action?.owner === "unassigned" ? "" : (action?.owner || ""), due: action?.due || "" });
  };
  const cancelEdit = () => {
    setEditingIdx(null);
    setEditDraft({ owner: "", due: "" });
  };
  const saveEdit = async (idx) => {
    if (savingEdit) return;
    setSavingEdit(true);
    try {
      const payload = { owner: editDraft.owner, due: editDraft.due || null };
      const { data } = await api.patch(`/notes/${note.id}/action-items/${idx}`, payload);
      const actions = [...((note.meeting_structured || {}).action_items || [])];
      actions[idx] = { ...actions[idx], ...(data?.action_item || {}) };
      applyChange({
        ...note,
        meeting_structured: { ...(note.meeting_structured || {}), action_items: actions },
      });
      toast.success("Action item updated.");
      cancelEdit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSavingEdit(false);
    }
  };

  // Iter 85.8 · Title-only edit path. Separate from owner/due so partners
  // can refine phrasing without touching metadata; the backend preserves
  // the original AI-organized text on `original_task` for provenance.
  const openTitleEdit = (idx, action) => {
    setTitleEditingIdx(idx);
    setTitleDraft(action?.task || "");
  };
  const cancelTitleEdit = () => {
    setTitleEditingIdx(null);
    setTitleDraft("");
  };
  const saveTitleEdit = async (idx) => {
    if (savingTitle) return;
    const next = (titleDraft || "").trim();
    if (!next) {
      toast.error("Title cannot be empty.");
      return;
    }
    setSavingTitle(true);
    try {
      const { data } = await api.patch(`/notes/${note.id}/action-items/${idx}`, { title: next });
      const actions = [...((note.meeting_structured || {}).action_items || [])];
      actions[idx] = { ...actions[idx], ...(data?.action_item || {}) };
      applyChange({
        ...note,
        meeting_structured: { ...(note.meeting_structured || {}), action_items: actions },
      });
      toast.success("Action item title updated.");
      cancelTitleEdit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Title update failed");
    } finally {
      setSavingTitle(false);
    }
  };

  const onSpawnBriefing = async (idx) => {
    if (spawningIdx !== null) return;
    setSpawningIdx(idx);
    try {
      const { data } = await api.post(`/notes/${note.id}/engagement-seeds/${idx}/spawn-briefing`);
      const seeds = [...((note.meeting_structured || {}).engagement_seeds || [])];
      seeds[idx] = {
        ...seeds[idx],
        spawned_briefing_id: data?.briefing?.id,
        spawned_at: new Date().toISOString(),
      };
      applyChange({
        ...note,
        meeting_structured: { ...(note.meeting_structured || {}), engagement_seeds: seeds },
      });
      toast.success(data?.already_spawned ? "Briefing already exists — link updated." : "Engagement briefing draft created.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Spawn briefing failed");
    } finally {
      setSpawningIdx(null);
    }
  };

  return (
    <BulkEditContext.Provider value={bulkCtx}>
    <div className="space-y-6" data-testid="organized-note-body">
      {/* Iter 85.82 · Edit-mode toolbar. Advisors requested a single
          "Edit" button at the top of the organized note that puts every
          section into edit mode at once; "Done" commits all dirty
          drafts in parallel via /notes/{id}/edit-text (audit history
          preserved section by section). Individual click-to-edit is
          still available when NOT in bulk mode. */}
      <div
        data-testid="organized-note-edit-toolbar"
        className={`flex items-center justify-between gap-3 border px-3 py-2 ${bulkEditing ? "border-brass bg-brass/10" : "border-primary/30 bg-primary/5"}`}
      >
        <div className="flex items-center gap-2 text-[0.6875rem] font-mono uppercase tracking-widest text-primary">
          <PencilSimple size={11} weight="bold" />
          {bulkEditing ? (
            <span data-testid="organized-note-edit-mode-hint" className="text-brass">Editing every section — type your corrections and click Done to save.</span>
          ) : (
            <span data-testid="organized-note-edit-hint">Click Edit to refine any section.</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {bulkEditing ? (
            <>
              <button
                type="button"
                data-testid="organized-note-cancel-edit"
                onClick={cancelBulk}
                disabled={committing}
                className="text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground px-2 py-1 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="organized-note-done-edit"
                onClick={commitBulk}
                disabled={committing}
                className="text-[0.6875rem] font-mono uppercase tracking-widest bg-brass text-white hover:opacity-90 px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Check size={11} weight="bold" /> {committing ? "Saving…" : "Done"}
              </button>
            </>
          ) : (
            <button
              type="button"
              data-testid="organized-note-enter-edit"
              onClick={enterBulk}
              className="text-[0.6875rem] font-mono uppercase tracking-widest bg-primary text-primary-foreground hover:opacity-90 px-3 py-1.5 inline-flex items-center gap-1.5"
            >
              <PencilSimple size={11} weight="bold" /> Edit
            </button>
          )}
        </div>
      </div>

      {/* Summary from the organizer — advisor can refine phrasing inline.
          Iter 85.84 · Hide empty scalar sections in read mode; surface
          them only while the advisor is in bulk Edit mode so they can
          be populated on demand. */}
      {(ms.summary || bulkEditing) && (
        <section data-testid="note-viewer-summary">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2">Summary</div>
          <div className="font-serif text-base leading-relaxed whitespace-pre-wrap">
            <EditableTextField
              noteId={note.id}
              path="summary"
              initialValue={ms.summary || ""}
              testIdPrefix="note-viewer-summary"
              placeholder="Add a summary…"
              onSaved={(next) => applyChange({
                ...note,
                meeting_structured: { ...(note.meeting_structured || {}), summary: next },
              })}
            />
          </div>
        </section>
      )}

      {/* Iter 85.55 · Engagement-briefing-specific sections. Rendered
          when a briefing Apply has mirrored accepted proposals onto
          `meeting_structured` — the source note becomes a readable
          record of the advisor-approved briefing output, while the
          canonical engagement.scope_summary/objectives/key_risks/
          playbook remain the operational source of truth. Data-driven:
          missing keys simply don't render.
          Iter 85.59 · Client-briefing-canonical fields
          (strategic_context / known_priorities / known_pain_points)
          render alongside — same data-driven approach so a single
          shared renderer covers every briefing kind without hard-coded
          note_type branching. Order: Client canon → Engagement canon →
          Playbook → Deliverables → shared sections below. */}
      {(ms.strategic_context || bulkEditing) && (
        <section data-testid="note-viewer-strategic-context">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2">Strategic context</div>
          <div className="font-serif text-sm leading-relaxed whitespace-pre-wrap">
            <EditableTextField
              noteId={note.id}
              path="strategic_context"
              initialValue={ms.strategic_context || ""}
              testIdPrefix="note-viewer-strategic-context"
              placeholder="Add strategic context…"
              onSaved={(next) => applyChange({ ...note, meeting_structured: { ...ms, strategic_context: next } })}
            />
          </div>
        </section>
      )}

      {(ms.known_priorities || []).length > 0 && (
        <section data-testid="note-viewer-known-priorities">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2">
            Known priorities ({ms.known_priorities.length})
          </div>
          <ul className="space-y-2">
            {ms.known_priorities.map((p, i) => (
              <li key={`prio-${i}`} className="border-l-2 border-primary/60 pl-3 font-serif text-sm leading-relaxed">
                <EditableTextField
                  noteId={note.id}
                  path={`known_priorities.${i}`}
                  initialValue={entryText(p)}
                  testIdPrefix={`note-viewer-priority-${i}`}
                  onSaved={(next) => {
                    const rows = [...(ms.known_priorities || [])];
                    rows[i] = typeof p === "string" ? next : { ...p, text: next };
                    applyChange({ ...note, meeting_structured: { ...ms, known_priorities: rows } });
                  }}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {(ms.known_pain_points || []).length > 0 && (
        <section data-testid="note-viewer-known-pain-points">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2">
            Pain points ({ms.known_pain_points.length})
          </div>
          <ul className="space-y-2">
            {ms.known_pain_points.map((p, i) => (
              <li key={`pain-${i}`} className="border-l-2 border-destructive/70 pl-3 font-serif text-sm leading-relaxed">
                <EditableTextField
                  noteId={note.id}
                  path={`known_pain_points.${i}`}
                  initialValue={entryText(p)}
                  testIdPrefix={`note-viewer-pain-point-${i}`}
                  onSaved={(next) => {
                    const rows = [...(ms.known_pain_points || [])];
                    rows[i] = typeof p === "string" ? next : { ...p, text: next };
                    applyChange({ ...note, meeting_structured: { ...ms, known_pain_points: rows } });
                  }}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {(ms.scope_summary || bulkEditing) && (
        <section data-testid="note-viewer-scope-summary">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2">Scope summary</div>
          <div className="font-serif text-sm leading-relaxed whitespace-pre-wrap">
            <EditableTextField
              noteId={note.id}
              path="scope_summary"
              initialValue={ms.scope_summary || ""}
              testIdPrefix="note-viewer-scope-summary"
              placeholder="Add scope summary…"
              onSaved={(next) => applyChange({ ...note, meeting_structured: { ...ms, scope_summary: next } })}
            />
          </div>
        </section>
      )}

      {(ms.objectives || []).length > 0 && (
        <section data-testid="note-viewer-objectives">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2">
            Objectives ({ms.objectives.length})
          </div>
          <ul className="space-y-2">
            {ms.objectives.map((o, i) => (
              <li key={`obj-${i}`} className="border-l-2 border-primary/60 pl-3 font-serif text-sm leading-relaxed">
                <EditableTextField
                  noteId={note.id}
                  path={`objectives.${i}`}
                  initialValue={entryText(o)}
                  testIdPrefix={`note-viewer-objective-${i}`}
                  onSaved={(next) => {
                    const rows = [...(ms.objectives || [])];
                    rows[i] = typeof o === "string" ? next : { ...o, text: next };
                    applyChange({ ...note, meeting_structured: { ...ms, objectives: rows } });
                  }}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {(ms.key_risks || []).length > 0 && (
        <section data-testid="note-viewer-key-risks">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2">
            Key risks ({ms.key_risks.length})
          </div>
          <ul className="space-y-2">
            {ms.key_risks.map((r, i) => (
              <li key={`risk-${i}`} className="border-l-2 border-destructive/70 pl-3 font-serif text-sm leading-relaxed">
                <EditableTextField
                  noteId={note.id}
                  path={`key_risks.${i}`}
                  initialValue={entryText(r)}
                  testIdPrefix={`note-viewer-key-risk-${i}`}
                  onSaved={(next) => {
                    const rows = [...(ms.key_risks || [])];
                    rows[i] = typeof r === "string" ? next : { ...r, text: next };
                    applyChange({ ...note, meeting_structured: { ...ms, key_risks: rows } });
                  }}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Iter 85.61 · Playbook Recommendations intentionally hidden from
          the organized note per firm policy — playbook state lives on
          the canonical engagement.playbook, not in the readable
          briefing record. Data is still mirrored in meeting_structured
          for audit/history; the section renderer is simply removed. */}

      {/* Iter 85.58 · Deliverables — concrete outputs Yadid has committed
          to produce. Data-driven: only renders when Organize or a Briefing
          Apply has surfaced at least one deliverable, either as a bare
          string (legacy) or as {title, source_briefing_id, canonical_id,
          owner, due_date, status, ...}. Iter 85.65 · Owner + Due + Status
          + Linked badge inline so the organized note carries execution
          detail alongside the narrative. */}
      {(ms.deliverables || []).length > 0 && (
        <section data-testid="note-viewer-deliverables">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2">
            Deliverables ({ms.deliverables.length})
          </div>
          <ul className="space-y-2">
            {ms.deliverables.map((d, i) => {
              const obj = typeof d === "object" && d ? d : {};
              const title = typeof d === "string" ? d : (obj.title || obj.text || entryText(d));
              const owner = obj.owner || null;
              const due = obj.due_date || null;
              const status = obj.status || null;
              const linked = !!obj.canonical_id;
              const isDelivered = status === "delivered";
              return (
                <li
                  key={obj.canonical_id || obj.source_briefing_id || `dv-${i}`}
                  data-testid={`note-viewer-deliverable-${i}`}
                  className="border-l-2 border-primary/60 pl-3 font-serif text-sm leading-relaxed"
                >
                  <div className={`flex items-start gap-2 flex-wrap ${isDelivered ? "text-muted-foreground" : ""}`}>
                    <span className={`flex-1 min-w-0 ${isDelivered ? "line-through" : ""}`}>
                      <EditableTextField
                        noteId={note.id}
                        path={`deliverables.${i}`}
                        initialValue={title}
                        testIdPrefix={`note-viewer-deliverable-${i}-title`}
                        onSaved={(next) => {
                          const rows = [...(ms.deliverables || [])];
                          rows[i] = typeof d === "string" ? next : { ...obj, title: next };
                          applyChange({ ...note, meeting_structured: { ...ms, deliverables: rows } });
                        }}
                      />
                    </span>
                    {linked && (
                      <span
                        className="text-[0.5625rem] font-mono uppercase tracking-widest text-primary bg-primary/10 border border-primary/40 px-1.5 py-0.5"
                        data-testid={`note-viewer-deliverable-${i}-linked`}
                        title="Linked to canonical Engagement Deliverable"
                      >
                        Linked
                      </span>
                    )}
                    {status && (
                      <span
                        className={`text-[0.5625rem] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${
                          isDelivered ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground"
                        }`}
                        data-testid={`note-viewer-deliverable-${i}-status`}
                      >
                        {status.replace(/_/g, " ")}
                      </span>
                    )}
                  </div>
                  {(owner || due) && (
                    <div className="mt-1 text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-3">
                      {owner && <span data-testid={`note-viewer-deliverable-${i}-owner`}>Owner · {owner}</span>}
                      {due && <span data-testid={`note-viewer-deliverable-${i}-due`}>Due · {due}</span>}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <AddItemRow
            onAdd={(v) => addItem("deliverables", v)}
            label="deliverable"
            testIdPrefix="note-viewer-deliverables"
          />
        </section>
      )}

      {/* Decisions */}
      {(ms.decisions || []).length > 0 && (
        <section data-testid="note-viewer-decisions">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2 flex items-center gap-1.5">
            <Lightning size={12} weight="fill" /> Decisions ({ms.decisions.length})
          </div>
          <ul className="space-y-2">
            {ms.decisions.map((d, i) => (
              <li key={i} className="border-l-2 border-primary/60 pl-3 font-serif text-sm leading-relaxed flex items-start gap-2 group">
                <span className="flex-1 min-w-0">
                  <EditableTextField
                    noteId={note.id}
                    path={`decisions.${i}`}
                    initialValue={entryText(d)}
                    testIdPrefix={`note-viewer-decision-${i}`}
                    onSaved={(next) => {
                      const rows = [...(ms.decisions || [])];
                      rows[i] = typeof d === "string" ? next : { ...d, text: next };
                      applyChange({ ...note, meeting_structured: { ...ms, decisions: rows } });
                    }}
                  />
                </span>
                <RejectButton
                  onClick={() => rejectItem("decisions", i, "decision")}
                  testId={`note-viewer-decision-${i}-reject`}
                  label="Remove this decision from the organized note"
                />
              </li>
            ))}
          </ul>
          <AddItemRow
            onAdd={(v) => addItem("decisions", v)}
            label="decision"
            testIdPrefix="note-viewer-decisions"
          />
        </section>
      )}

      {/* Action items — interactive: completion toggle, promote-to-task, inline edit */}
      {(ms.action_items || []).length > 0 && (
        <section data-testid="note-viewer-actions">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2 flex items-center gap-1.5">
            <ListChecks size={12} weight="fill" /> Action items ({ms.action_items.length})
          </div>
          <ul className="space-y-2">
            {ms.action_items.map((a, i) => {
              const done = !!a.done;
              const Icon = done ? CheckCircle : Circle;
              const focused = i === focusCommitmentIdx;
              const promoted = !!a.promoted_task_id;
              const toggling = togglingIdx === i;
              return (
                <li
                  key={i}
                  ref={(el) => { if (el) refs.current[i] = el; }}
                  data-testid={`note-viewer-action-item-${i}`}
                  className={`flex items-start gap-2 text-sm px-2 py-1.5 -mx-2 transition-colors ${
                    focused ? "bg-brass/15 ring-2 ring-brass/70" : ""
                  }`}
                  aria-current={focused ? "true" : undefined}
                >
                  {/* Iter 80 · Clickable completion toggle. Was display-only
                      before; the /complete endpoint was already reachable
                      via Task promotion sync, but partners have long asked
                      to tick items right in the Note Viewer. */}
                  <button
                    type="button"
                    data-testid={`note-viewer-action-item-${i}-toggle`}
                    onClick={() => toggleComplete(i, done)}
                    disabled={toggling}
                    aria-pressed={done}
                    aria-label={done ? "Mark as not done" : "Mark as done"}
                    title={done ? "Mark as not done" : "Mark as done"}
                    className="mt-1 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-40"
                  >
                    <Icon
                      size={14}
                      weight={done ? "fill" : "regular"}
                      className={done ? "text-emerald-500" : "text-muted-foreground hover:text-primary"}
                    />
                  </button>
                  <div className="flex-1 min-w-0">
                    {bulkEditing ? (
                      <ActionItemBulkEditor
                        noteId={note.id}
                        idx={i}
                        action={a}
                        onSaved={(ai) => {
                          if (!ai) return;
                          const actions = [...((note.meeting_structured || {}).action_items || [])];
                          actions[i] = { ...actions[i], ...ai };
                          applyChange({ ...note, meeting_structured: { ...(note.meeting_structured || {}), action_items: actions } });
                        }}
                      />
                    ) : (
                    <>
                    <div className="flex items-start justify-between gap-2">
                      {titleEditingIdx === i ? (
                        <form
                          data-testid={`note-viewer-action-item-${i}-title-form`}
                          className="flex-1 flex flex-wrap items-center gap-2"
                          onSubmit={(e) => { e.preventDefault(); saveTitleEdit(i); }}
                          onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); cancelTitleEdit(); } }}
                        >
                          <input
                            type="text"
                            data-testid={`note-viewer-action-item-${i}-title-input`}
                            value={titleDraft}
                            onChange={(e) => setTitleDraft(e.target.value)}
                            className="flex-1 min-w-0 bg-background border border-border rounded-none px-2 py-1 text-sm"
                            maxLength={500}
                            autoFocus
                          />
                          <button
                            type="submit"
                            data-testid={`note-viewer-action-item-${i}-title-save`}
                            disabled={savingTitle}
                            className="text-[0.625rem] font-mono uppercase tracking-widest text-brass hover:opacity-80 disabled:opacity-40 px-2"
                          >
                            {savingTitle ? "Saving…" : "Save"}
                          </button>
                          <button
                            type="button"
                            data-testid={`note-viewer-action-item-${i}-title-cancel`}
                            onClick={cancelTitleEdit}
                            className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground px-2"
                          >
                            Cancel
                          </button>
                        </form>
                      ) : (
                        <button
                          type="button"
                          data-testid={`note-viewer-action-item-${i}-title`}
                          onClick={() => openTitleEdit(i, a)}
                          className={`group flex-1 min-w-0 text-left inline-flex items-start gap-1.5 rounded-none px-1 -mx-1 py-0.5 hover:bg-primary/5 focus:bg-primary/5 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/40 transition-colors ${done ? "line-through text-muted-foreground" : ""}`}
                          title="Click to refine the action item's title — syncs to linked Task"
                        >
                          <span className="min-w-0 flex-1 whitespace-pre-wrap">{a.task}</span>
                          <span
                            aria-hidden="true"
                            className="opacity-40 group-hover:opacity-100 group-focus:opacity-100 transition-opacity text-primary shrink-0 mt-0.5"
                          >
                            <PencilSimple size={12} weight="bold" />
                          </span>
                        </button>
                      )}
                      {promoted ? (
                        <Link
                          to={`/tasks?highlight=${a.promoted_task_id}`}
                          data-testid={`note-viewer-action-item-${i}-task-link`}
                          className="shrink-0 text-[0.625rem] font-mono uppercase tracking-widest text-brass hover:opacity-80 flex items-center gap-1"
                        >
                          <Briefcase size={11} weight="fill" /> Task ↗
                        </Link>
                      ) : (
                        <button
                          type="button"
                          data-testid={`note-viewer-action-item-${i}-promote`}
                          onClick={() => onPromoteToTask(i)}
                          disabled={promotingIdx === i}
                          className="shrink-0 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-brass disabled:opacity-50 flex items-center gap-1"
                          title="Create a linked Task. Completion syncs both ways; other edits stay on the Task."
                        >
                          <ArrowSquareOut size={11} /> {promotingIdx === i ? "Promoting…" : "Promote to Task"}
                        </button>
                      )}
                      {!promoted && !done && (
                        <RejectButton
                          onClick={() => rejectItem("action_items", i, "action item")}
                          testId={`note-viewer-action-item-${i}-reject`}
                          label="Remove this action item — Re-organize won't bring it back"
                        />
                      )}
                    </div>
                    {editingIdx === i ? (
                      <form
                        data-testid={`note-viewer-action-item-${i}-edit-form`}
                        className="mt-1 flex flex-wrap items-center gap-2 text-[0.625rem] font-mono"
                        onSubmit={(e) => { e.preventDefault(); saveEdit(i); }}
                        onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); cancelEdit(); } }}
                      >
                        <input
                          type="text"
                          data-testid={`note-viewer-action-item-${i}-owner-input`}
                          value={editDraft.owner}
                          onChange={(e) => setEditDraft((d) => ({ ...d, owner: e.target.value }))}
                          placeholder="owner"
                          className="bg-background border border-border rounded-none px-2 py-1 text-xs font-sans normal-case tracking-normal w-40"
                          autoFocus
                        />
                        <input
                          type="date"
                          data-testid={`note-viewer-action-item-${i}-due-input`}
                          value={editDraft.due || ""}
                          onChange={(e) => setEditDraft((d) => ({ ...d, due: e.target.value }))}
                          className="bg-background border border-border rounded-none px-2 py-1 text-xs font-sans normal-case tracking-normal"
                        />
                        <button
                          type="submit"
                          data-testid={`note-viewer-action-item-${i}-edit-save`}
                          disabled={savingEdit}
                          className="uppercase tracking-widest text-brass hover:opacity-80 disabled:opacity-40 px-2"
                        >
                          {savingEdit ? "Saving…" : "Save"}
                        </button>
                        <button
                          type="button"
                          data-testid={`note-viewer-action-item-${i}-edit-cancel`}
                          onClick={cancelEdit}
                          className="uppercase tracking-widest text-muted-foreground hover:text-foreground px-2"
                        >
                          Cancel
                        </button>
                      </form>
                    ) : (
                      <button
                        type="button"
                        data-testid={`note-viewer-action-item-${i}-edit-open`}
                        onClick={() => openEdit(i, a)}
                        className="group inline-flex items-center gap-1.5 mt-0.5 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground hover:bg-primary/5 focus:bg-primary/5 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/40 rounded-none px-1 -mx-1 py-0.5 transition-colors"
                        title="Click to edit owner and due date — syncs to linked Task"
                      >
                        <span className="inline-flex items-center gap-1">
                          {a.owner && <span>owner {a.owner}</span>}
                          {a.owner && a.due && <span> · </span>}
                          {a.due && <span>due {a.due}</span>}
                          {!a.owner && !a.due && <span className="italic">add owner / due</span>}
                        </span>
                        <span
                          aria-hidden="true"
                          className="opacity-40 group-hover:opacity-100 group-focus:opacity-100 transition-opacity text-primary shrink-0"
                        >
                          <PencilSimple size={10} weight="bold" />
                        </span>
                      </button>
                    )}
                    {/* Iter 85.8 · Provenance — when a partner has refined
                        the title, keep the original AI-organized text
                        one hairline below so the audit trail is visible
                        without cluttering the primary read. */}
                    {a.original_task && a.original_task !== a.task && (
                      <div
                        data-testid={`note-viewer-action-item-${i}-provenance`}
                        className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground/70 mt-1"
                        title="Original AI-organized text preserved for audit"
                      >
                        <span className="text-muted-foreground">Original ·</span>{" "}
                        <span className="normal-case tracking-normal font-sans italic">{a.original_task}</span>
                      </div>
                    )}
                    </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          <AddItemRow
            onAdd={(v) => addItem("action_items", v)}
            label="action item"
            testIdPrefix="note-viewer-actions"
          />
        </section>
      )}

      {/* Key points */}
      {(ms.key_points || []).length > 0 && (
        <section data-testid="note-viewer-key-points">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2 flex items-center gap-1.5">
            <NoteIcon size={12} weight="fill" /> Key points ({ms.key_points.length})
          </div>
          <ul className="space-y-2">
            {ms.key_points.map((k, i) => {
              const { title, body } = keyPointParts(k);
              return (
                <li key={i} className="border-l-2 border-primary/40 pl-3 font-serif text-sm leading-relaxed flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    {title && (
                      <div className="font-semibold not-italic mb-1">
                        <EditableTextField
                          noteId={note.id}
                          path={`key_points.${i}`}
                          field="title"
                          multiline={false}
                          initialValue={title}
                          testIdPrefix={`note-viewer-key-point-${i}-title`}
                          onSaved={(next) => {
                            const rows = [...(ms.key_points || [])];
                            const cur = rows[i];
                            rows[i] = typeof cur === "string"
                              ? { title: next, body: cur }
                              : { ...(cur || {}), title: next };
                            applyChange({ ...note, meeting_structured: { ...ms, key_points: rows } });
                          }}
                        />
                      </div>
                    )}
                    <div>
                      <EditableTextField
                        noteId={note.id}
                        path={`key_points.${i}`}
                        field="body"
                        initialValue={body}
                        testIdPrefix={`note-viewer-key-point-${i}-body`}
                        onSaved={(next) => {
                          const rows = [...(ms.key_points || [])];
                          const cur = rows[i];
                          rows[i] = typeof cur === "string"
                            ? { body: next }
                            : { ...(cur || {}), body: next };
                          applyChange({ ...note, meeting_structured: { ...ms, key_points: rows } });
                        }}
                      />
                    </div>
                  </div>
                  <RejectButton
                    onClick={() => rejectItem("key_points", i, "key point")}
                    testId={`note-viewer-key-point-${i}-reject`}
                    label="Remove this key point — Re-organize won't bring it back"
                  />
                </li>
              );
            })}
          </ul>
          <AddItemRow
            onAdd={(v) => addItem("key_points", v)}
            label="key point"
            testIdPrefix="note-viewer-key-points"
          />
        </section>
      )}

      {/* Open questions — Iter 81 · each row now carries an optional
          Embrace movement chip + resolve/reopen toggle. Text stays the
          evidence; movement/lens/resolved live alongside as metadata. */}
      {(ms.open_questions || []).length > 0 && (
        <section data-testid="note-viewer-open-questions">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2 flex items-center gap-1.5">
            <Question size={12} weight="fill" /> Open questions ({ms.open_questions.length})
          </div>
          <ul className="space-y-2">
            {ms.open_questions.map((q, i) => (
              <OpenQuestionRow
                key={i}
                idx={i}
                entry={q}
                noteId={note.id}
                onReject={() => rejectItem("open_questions", i, "open question")}
                onTextSaved={(next) => {
                  const list = [...((note.meeting_structured || {}).open_questions || [])];
                  list[i] = typeof q === "string" ? next : { ...q, text: next };
                  applyChange({
                    ...note,
                    meeting_structured: { ...(note.meeting_structured || {}), open_questions: list },
                  });
                }}
                onLocalUpdate={(next) => {
                  const list = [...((note.meeting_structured || {}).open_questions || [])];
                  list[i] = next;
                  applyChange({
                    ...note,
                    meeting_structured: { ...(note.meeting_structured || {}), open_questions: list },
                  });
                }}
              />
            ))}
          </ul>
          <AddItemRow
            onAdd={(v) => addItem("open_questions", v)}
            label="open question"
            testIdPrefix="note-viewer-open-questions"
          />
        </section>
      )}

      {/* Follow-ups */}
      {(ms.follow_ups || []).length > 0 && (
        <section data-testid="note-viewer-followups">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2 flex items-center gap-1.5">
            <ArrowSquareOut size={12} weight="fill" /> Follow-ups ({ms.follow_ups.length})
          </div>
          <ul className="space-y-2">
            {ms.follow_ups.map((f, i) => (
              <li key={i} className="border-l-2 border-primary/40 pl-3 font-serif text-sm leading-relaxed flex items-start gap-2">
                <span className="flex-1 min-w-0">
                  <EditableTextField
                    noteId={note.id}
                    path={`follow_ups.${i}`}
                    initialValue={entryText(f)}
                    testIdPrefix={`note-viewer-followup-${i}`}
                    onSaved={(next) => {
                      const rows = [...(ms.follow_ups || [])];
                      rows[i] = typeof f === "string" ? next : { ...f, text: next };
                      applyChange({ ...note, meeting_structured: { ...ms, follow_ups: rows } });
                    }}
                  />
                </span>
                <RejectButton
                  onClick={() => rejectItem("follow_ups", i, "follow-up")}
                  testId={`note-viewer-followup-${i}-reject`}
                  label="Remove this follow-up — Re-organize won't bring it back"
                />
              </li>
            ))}
          </ul>
          <AddItemRow
            onAdd={(v) => addItem("follow_ups", v)}
            label="follow-up"
            testIdPrefix="note-viewer-followups"
          />
        </section>
      )}

      {/* Suggested engagements */}
      {(ms.engagement_seeds || []).length > 0 && (
        <section data-testid="note-viewer-engagement-seeds">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2 flex items-center gap-1.5">
            <Briefcase size={12} weight="fill" /> Suggested engagements ({ms.engagement_seeds.length})
          </div>
          <ul className="space-y-2">
            {ms.engagement_seeds.map((s, i) => {
              const spawned = !!s?.spawned_briefing_id;
              return (
                <li key={i} className="border-l-2 border-primary/40 pl-3 font-serif text-sm leading-relaxed flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold not-italic">{s?.name || ""}</div>
                    {s?.scope_hint && <div className="text-muted-foreground mt-0.5">{s.scope_hint}</div>}
                  </div>
                  {spawned ? (
                    <Link
                      to={`/notes?open=${s.spawned_briefing_id}`}
                      data-testid={`note-viewer-engagement-seed-${i}-briefing-link`}
                      className="shrink-0 text-[0.625rem] font-mono uppercase tracking-widest text-brass hover:opacity-80 flex items-center gap-1 mt-1"
                    >
                      <Briefcase size={11} weight="fill" /> Briefing ↗
                    </Link>
                  ) : (
                    <button
                      type="button"
                      data-testid={`note-viewer-engagement-seed-${i}-spawn`}
                      onClick={() => onSpawnBriefing(i)}
                      disabled={spawningIdx === i}
                      className="shrink-0 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-brass disabled:opacity-50 flex items-center gap-1 mt-1"
                      title="Create a draft engagement_briefing note from this suggestion"
                    >
                      <ArrowSquareOut size={11} /> {spawningIdx === i ? "Spawning…" : "Spawn briefing"}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
    </BulkEditContext.Provider>
  );
}
